
import { useState, useEffect } from "react";
import { Flex, Button, Input, Select, Form, Space, Popconfirm, Checkbox, Popover, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { spaceColorBgMapping, spaceIconsConfig } from "../../utils/colorBgColorMap.js";
import SvgIcons from "../layout/assets/SvgIcons.jsx";
import { FORM_LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from "../../utils/formTranslations.js";

const { TextArea } = Input;

const General = (props) => {
  const { spaceData, handleSpaceChange, submited, onSubmit, categories, masterList = [], formValues={}, form } = props;

  const { t } = useTranslation();
  const [showColorPicker, setShowColorPicker] = useState(false)

  // `masterToRestrictAccess` is a list of dataIds; each one gets its own column
  // picker, held under the `masterColumns` object keyed by dataId.
  const selectedMasterIds = Array.isArray(formValues.masterToRestrictAccess)
    ? formValues.masterToRestrictAccess
    : (formValues.masterToRestrictAccess ? [formValues.masterToRestrictAccess] : [])
  const selectedMasters = selectedMasterIds
    .map(dataId => masterList?.find(m => m.dataId === dataId))
    .filter(Boolean)

  // Which language the Space Name / Description fields are authoring. The default
  // language edits the canonical values (the `name`/`description` form fields);
  // other languages edit spaceData.translations[lng] (saved via handleSpaceChange
  // since saveSpace posts { ...spaceData, ...values }).
  const [editLang, setEditLang] = useState(DEFAULT_LANGUAGE)
  const isDefaultLang = editLang === DEFAULT_LANGUAGE
  const translations = spaceData?.translations || {}

  const setTranslationField = (key, val) => {
    const updated = {
      ...translations,
      [editLang]: { ...(translations[editLang] || {}), [key]: val },
    }
    handleSpaceChange("translations", updated)
  }

  // A master with exactly one field offers no real choice, so its column is filled
  // in for it. This watches masterList too because the fields only arrive once the
  // master list has loaded, which can be after the space data.
  useEffect(() => {
    if (!selectedMasters.length) return;
    const current = form.getFieldValue("masterColumns") || {};
    const next = { ...current };
    let changed = false;
    selectedMasters.forEach(master => {
      if (!next[master.dataId] && master?.fields?.length === 1) {
        next[master.dataId] = master.fields[0].label;
        changed = true;
      }
    });
    if (changed) {
      form.setFieldsValue({ masterColumns: next });
    }
  }, [formValues?.masterToRestrictAccess, masterList])

  useEffect(() => {
    if (submited) {
      form.submit()
      onSubmit()
    }
  }, [submited])

  const handleRemove = () => {
    if (spaceData.logoImage) {
      props.onChange(true);
    }
  };

  const handleIconColorChange = (key, val) => {
    handleSpaceChange(key, val)
  }

  const colorIconPicker = (
    <div className="xl:w-94 lg:w-80 sm:w-36 md:w-56 color-icon-picker">
      <div className="picker-header">
        <div className="text-weight-600">{t('Pick color and icon')}</div>
        <CloseOutlined onClick={() => setShowColorPicker(false)} />
      </div>
      <div className="color-row">
        {
          ["#F5222D", "#FA8C16", "#FAAD14", "#84CC16", "#50A248", "#13C2C2", "#0A99FE", "#141BCB", "#722ED1", "#EB2F96"].map(color => {
            return <div className="color-box" style={{ backgroundColor: color }} onClick={() => handleIconColorChange("spaceColor", color)}>{spaceData.spaceColor === color && <CheckOutlined style={{ width: "16px" }} className="text-white text-sm" />}</div>
          })
        }
      </div>
      <div className="icon-picker">
        {
          spaceIconsConfig.map(icon => {
            return <div className="icon-picker-box" onClick={() => handleIconColorChange("spaceIcon", icon)}>
              <span style={{ color: spaceData.spaceIcon === icon ? spaceData.spaceColor : "#00000073", display: "flex" }}>
                <SvgIcons key={"color-" + spaceData.spaceColor} type={icon} />

              </span>
            </div>

          })
        }
      </div>
    </div>
  )

  const filterOption = (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  return (
    <div style={{ background: "#fff", borderRadius: "1rem" }}>
      <div className="space-setting" >
        <div className="space-heading" >{t("Space Configuration")}</div>
        <Form.Item style={{ marginBottom: "32px" }}>
          <Flex align="center"  >
            <Space>
              <div className="space-icon-box" style={{ backgroundColor: spaceColorBgMapping[spaceData.spaceColor] || "#f5eeff" }}>
                <span style={{ color: spaceData.spaceColor || "#722ed1", display: "flex" }}>
                  <SvgIcons key={"color-" + spaceData.spaceColor} type={spaceData.spaceIcon} />
                </span>
              </div>
              <Popover content={colorIconPicker} trigger={"click"} open={showColorPicker} onOpenChange={() => setShowColorPicker(true)} placement="right">
                <Button style={{ fontWeight: "400", marginRight: "4px" }} type="default">{t("Pick icon and color")}</Button>
              </Popover>
            </Space>
          </Flex>
        </Form.Item>
        <Form.Item>
          <Select
            value={editLang}
            onChange={setEditLang}
            options={FORM_LANGUAGE_OPTIONS}
          />
          {!isDefaultLang && (
            <div className="text-color-secondary" style={{ marginTop: 4, fontSize: 12 }}>
              {t("Editing translation — only the space name and description are saved for this language. Other settings are shared across languages.")}
            </div>
          )}
        </Form.Item>

        {
          isDefaultLang ? (
            <>
              <Form.Item
                label={t("Space Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("Space name is required"),
                  },
                ]}
              >
                <Input placeholder={t("Space name")} />
              </Form.Item>
              <Form.Item label={t("Space Description")} name="description">
                <TextArea placeholder={t("Description")} rows={2} />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item label={t("Space Name")} required>
                <Input
                  placeholder={t("Space name")}
                  value={translations[editLang]?.name || ""}
                  onChange={(e) => setTranslationField("name", e.target.value)}
                />
              </Form.Item>
              <Form.Item label={t("Space Description")}>
                <TextArea
                  placeholder={t("Description")}
                  rows={2}
                  value={translations[editLang]?.description || ""}
                  onChange={(e) => setTranslationField("description", e.target.value)}
                />
              </Form.Item>
            </>
          )
        }
        <Form.Item
          label={t("Default Language")}
          name="defaultLanguage"
          rules={[
            {
              required: true,
              message: t("Please select the default language for this space"),
            },
          ]}
        >
          <Select
            placeholder={t("Please select the default language for this space")}
            style={{}}
          >
            <Select.Option value="English">{t('English')}</Select.Option>
            {/* <Select.Option value="Japanese">Japanese</Select.Option> */}
          </Select>
        </Form.Item>

        <Form.Item
          label={t("Space Abbreviation")}
          name="abbreviation"
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("Space abbreviation is required"),
            },
          ]}
        >
          <Flex gap="middle">
            <Form.Item noStyle name="abbreviation">
              <Input placeholder={t("Space abbreviation")} />
            </Form.Item>
          </Flex>
        </Form.Item>

        <Form.Item
          label={t("ID Type")}
          name="idType"
          rules={[
            {
              required: true,
              message: t("Please select the id type for this space"),
            },
          ]}
        >
          <Select
            placeholder={t("Please select the id type for this space")}
            style={{}}
          >
            <Select.Option value="sequential">{t("Sequential")}</Select.Option>
            <Select.Option value="random">{t("Random")}</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="category" label={t("Space Category Tags")}>
          <Select mode="multiple" allowClear options={categories} placeholder={t("Select Categories")} showSearch filterOption={filterOption} />
        </Form.Item>

        <Form.Item name="masterToRestrictAccess" label={t("Master Data to Restrict Access")} >
          <Select mode="multiple" allowClear options={masterList ? masterList.map(data => ({ label: data.dataName, value: data.dataId })) : []} placeholder={t("Select master data to restrict request access")} showSearch filterOption={filterOption}
            onChange={(value) => {
              const nextIds = value || [];
              const current = form.getFieldValue("masterColumns") || {};
              // Keep the columns of the masters still selected, drop the rest, and
              // pre-fill any newly added master that has a single field.
              const next = {};
              nextIds.forEach(dataId => {
                const master = masterList?.find(m => m.dataId === dataId);
                next[dataId] = current[dataId]
                  || (master?.fields?.length === 1 ? master.fields[0].label : undefined);
              });
              form.setFieldValue("masterColumns", next);
            }}
          />
        </Form.Item>

        {selectedMasters.map(master => (
          <Form.Item
            key={master.dataId}
            name={["masterColumns", master.dataId]}
            label={`${master.dataName} - ${t("Column to Restrict Access")}`}
            rules={[{ required: true, message: t("Please select a column") }]}
          >
            <Select
              allowClear
              options={master?.fields?.map(f => ({ label: f.label, value: f.label })) || []}
              placeholder={t("Select column")}
              showSearch
              filterOption={filterOption}
            />
          </Form.Item>
        ))}
      </div>
    </div>
  );
};
export default General;
